// schema.org Product/Offer structured data for the product-detail page.
//
// Emitted as a <script type="application/ld+json"> element so search engines can
// render rich product results (name, price, availability). It is metadata only —
// invisible to users and never a substitute for the on-page compliance copy, so
// it deliberately carries no health/benefit claims (brand §7 / RAD §13).

// ProductSeo is the subset of the product-detail response the builder needs.
// Structurally satisfied by the generated `ProductDetail` type.
export interface ProductSeo {
  slug: string;
  name: string;
  description?: string;
  sku?: string;
  priceCents: number;
  currency: string;
  imageUrl?: string;
  images?: string[];
  inStock: boolean;
}

const SCRIPT_ID = 'product-structured-data';

// productJsonLd builds a schema.org Product node with a nested Offer. `origin`
// is injected (rather than read from `location`) so the builder stays pure and
// testable; the caller passes `location.origin`.
export function productJsonLd(p: ProductSeo, origin: string): Record<string, unknown> {
  const gallery = (p.images && p.images.length > 0 ? p.images : p.imageUrl ? [p.imageUrl] : [])
    .filter((src): src is string => Boolean(src));

  const offer: Record<string, unknown> = {
    '@type': 'Offer',
    // Google requires the price as a plain decimal, no currency symbol.
    price: (p.priceCents / 100).toFixed(2),
    priceCurrency: p.currency,
    availability: p.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    url: `${origin}/products/${encodeURIComponent(p.slug)}`,
  };

  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    brand: { '@type': 'Brand', name: 'Asmaluz' },
    offers: offer,
  };
  if (p.description) data.description = p.description;
  if (p.sku) data.sku = p.sku;
  if (gallery.length > 0) data.image = gallery;
  return data;
}

// injectProductJsonLd writes the structured data into <head>, replacing any
// previously-injected node so stale product data can never linger on the page.
export function injectProductJsonLd(data: Record<string, unknown>): void {
  removeProductJsonLd();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = SCRIPT_ID;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

// removeProductJsonLd tears the structured data back down — called on unmount so
// it does not leak onto the next route.
export function removeProductJsonLd(): void {
  document.getElementById(SCRIPT_ID)?.remove();
}
